/**
 * Feature-tagged debug logging via the `debug` npm package.
 *
 * Activated by setting `localStorage.debug = 'dw:*'` in the browser console.
 * Namespace convention: `dw:<feature>:<concern>` (e.g. `dw:chat:dedup`).
 *
 * In dev mode, all `debug()` calls are also batched and forwarded to
 * `POST /api/debug-log` so backend + frontend events land in a single
 * `nad-debug.log` timeline.  The transport is best-effort (fire-and-forget).
 *
 * During Playwright tests, all `debug` output goes to `console.log` which
 * Playwright captures into `browser.log`.
 *
 * ## Structured JSON logging (NAD transport)
 *
 * When `VITE_NAD_DEBUG` is set, each `debug()` call also emits a parallel
 * `[nad-json]`-prefixed JSON line via `console.log`.  This allows
 * `trace-summary.py --browser browser.log` to parse browser events alongside
 * backend structlog JSON lines.
 *
 * The returned debug function accepts two calling conventions:
 *
 * 1. **Printf-style** (backward compatible):
 *    ```ts
 *    log('state dedup: prev=%d incoming=%d', prev.length, incoming.length)
 *    ```
 *
 * 2. **Structured** (new API for NAD):
 *    ```ts
 *    log({ event: 'state-dedup', prev: 5, incoming: 3 })
 *    ```
 *    When the first arg is an object with an `event` field, it is emitted
 *    as structured JSON with namespace-derived `feature`/`concern` fields.
 */
import createDebug from 'debug'
import * as Y from 'yjs'
import { useMemo } from 'react'

export { default as createDebug } from 'debug'

type DebugValue = string | number | boolean | null | object | Error | undefined
type DebugRecord = Record<string, DebugValue>
type StructuredDebugRecord = DebugRecord & { event: string }

// ---------------------------------------------------------------------------
// Structured JSON emitter — [nad-json] prefix for trace-summary.py
// ---------------------------------------------------------------------------

/** Parse `dw:feature:concern` into separate fields. */
function _parseNamespace(ns: string): { feature?: string; concern?: string } {
  const parts = ns.split(':')
  // Convention: dw:<feature>:<concern> or <feature>:<concern>
  if (parts[0] === 'dw') {
    return { feature: parts[1], concern: parts.slice(2).join(':') || undefined }
  }
  return { feature: parts[0], concern: parts.slice(1).join(':') || undefined }
}

/** Safely summarize a value for JSON output — never include huge blobs. */
function _summarizeValue(v: DebugValue, maxStr = 120): DebugValue {
  if (v === undefined) return undefined
  if (v === null) return null
  if (typeof v === 'number' || typeof v === 'boolean') return v
  if (typeof v === 'string') {
    return v.length > maxStr ? v.slice(0, maxStr) + `...(${v.length})` : v
  }
  if (Array.isArray(v)) return `[${v.length} items]`
  if (typeof v === 'object') {
    // For small objects, include them; for large ones, summarize
    try {
      const s = JSON.stringify(v)
      if (s.length <= maxStr) return v
      return `{${Object.keys(v).length} keys}`
    } catch {
      return String(v)
    }
  }
  return String(v)
}

/**
 * Emit a structured JSON line with `[nad-json]` prefix.
 * Playwright captures `console.log` calls into `browser.log`, so these
 * lines become parseable by `trace-summary.py --browser`.
 */
function _emitNadJson(ns: string, data: DebugRecord, priority = false): void {
  const { feature, concern } = _parseNamespace(ns)
  const entry: DebugRecord = {
    event: data.event ?? ns,
    ns,
    timestamp: new Date().toISOString(),
    ...data,
  }
  if (feature) entry.feature = feature
  if (concern) entry.concern = concern
  _enqueue(ns, [entry], priority)
}

/**
 * Build structured data from printf-style args.
 * Extracts key=value pairs from the format string when possible.
 */
function _extractFromPrintfArgs(
  formatStr: string,
  args: DebugValue[],
): DebugRecord {
  const data: DebugRecord = { event: formatStr }

  // Try to extract named values from "key=%s key2=%d" patterns
  const re = /(\w+)=%[sdoOfj]/g
  let match: RegExpExecArray | null
  let argIdx = 0
  while ((match = re.exec(formatStr)) !== null && argIdx < args.length) {
    data[match[1]] = _summarizeValue(args[argIdx])
    argIdx++
  }

  // If no patterns matched, include remaining args positionally
  if (argIdx === 0 && args.length > 0) {
    for (let i = 0; i < args.length; i++) {
      data[`arg${i}`] = _summarizeValue(args[i])
    }
  }

  return data
}

export function useDebugLog(namespace: string) {
  return useMemo(() => createDebug(namespace), [namespace])
}

// ---------------------------------------------------------------------------
// Dev-only: forward browser debug() calls to backend nad-debug.log
// ---------------------------------------------------------------------------

let _clientID: number | undefined

/** Call once after Yjs provider connects to tag all forwarded entries. */
export function setDebugClientID(id: number) {
  _clientID = id
}

interface LogEntry {
  ts: number
  ns: string
  args: DebugValue[]
  clientID?: number
}

const _buffer: LogEntry[] = []
let _timer: ReturnType<typeof setTimeout> | null = null
const FLUSH_INTERVAL_MS = 500
const MAX_BATCH = 200
const MAX_BUFFER = 1000

// Flag to suppress [nad-json] emission from the createDebug.log override
// when the call originates from createNadDebug (which handles its own emission).
let _suppressOverrideNadJson = false

function _safeSerialize(v: DebugValue): DebugValue {
  if (v === undefined) return '[undefined]'
  if (v instanceof Error) return { name: v.name, message: v.message }
  try {
    JSON.stringify(v)
    return v
  } catch {
    return String(v)
  }
}

function _flush() {
  if (_timer) clearTimeout(_timer)
  _timer = null
  if (!_buffer.length) return
  const batch = _buffer.splice(0, MAX_BATCH)
  fetch('/api/debug-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(batch),
    // Bulk debug batches can exceed the browser's 64 KiB keepalive budget.
    // Use normal requests so a successful enqueue actually reaches NAD.
  }).catch(() => {})
  if (_buffer.length) _timer = setTimeout(_flush, FLUSH_INTERVAL_MS)
}

const _NOISY_NS = new Set([
  'dw:size:registry',
  'dw:size:designer-shell',
  // These namespaces fire during normal canvas read/layout passes.
  'dw:canvas:status',
  'dw:layout:dimension-sync',
  'dw:layout:position',
  'dw:group:add-position',
  // Per-node render-state diagnostics are useful when tracing a specific
  // issue, but too noisy for the default NAD transport.
  'dw:voice-profile-source:data',
  'dw:video-variant-sync',
  'dw:video-clip-label',
  'dw:video-generator-audio-default',
  // Thumbnail resolution runs during normal canvas renders. Keep it out of
  // nad-debug.log so opening editor panels does not look like YDoc churn.
  'dw:library-display:thumbnail',
])

function _enqueue(ns: string, args: DebugValue[], priority = false) {
  if (_NOISY_NS.has(ns)) return
  // Preserve explicitly important decisions during noisy canvas renders.
  // Flush one bounded batch before admitting them; ordinary logs stay bounded.
  if (priority && _buffer.length >= MAX_BUFFER) _flush()
  if (_buffer.length >= MAX_BUFFER) return
  _buffer.push({
    ts: Date.now(),
    ns,
    args: args.map(_safeSerialize),
    clientID: _clientID,
  })
  if (!_timer) _timer = setTimeout(_flush, FLUSH_INTERVAL_MS)
}

if (import.meta.env?.VITE_NAD_DEBUG) {
  // --- debug() log override: forward all debug() calls to backend ---
  createDebug.log = function (this: { namespace?: string }, ...args: DebugValue[]) {
    const ns = this?.namespace ?? 'unknown'
    if (_NOISY_NS.has(ns)) return
    _enqueue(ns, args)

    // --- NAD structured JSON emitter ---
    // In debug's log override, args[0] is always the already-formatted string.
    // Structured calls via createNadDebug() emit their own [nad-json] lines
    // and set _suppressOverrideNadJson to avoid double emission.
    if (!_suppressOverrideNadJson) {
      const firstArg = args[0]
      if (typeof firstArg === 'string') {
        const data = _extractFromPrintfArgs(firstArg, args.slice(1))
        _emitNadJson(ns, data)
      }
    }
  }
  // Enable everything EXCEPT the namespaces already declared too noisy for
  // the NAD transport. They are equally unfit for the console: they fire
  // during normal canvas read/layout passes, not on user actions.
  //
  // `dw:group:add-position` alone is emitted five times inside
  // deriveGroupedNodeLayout, which runs on every layout pass of every group.
  // On a nine-group, ~100-node canvas that reached ~5,500 console.debug
  // calls a second. Each one makes V8 capture a stack when DevTools is
  // attached: a performance trace of a marquee drag recorded 39,359 stack
  // captures and ~116 dropped frames per second, which presented as the
  // canvas flickering while selecting.
  //
  // `debug` honours `-` exclusions, so these stay opt-in: set
  // `localStorage.debug` explicitly to get them back.
  const _noisyExclusions = Array.from(_NOISY_NS, (ns) => `-${ns}`).join(',')
  createDebug.enable(_noisyExclusions ? `dw:*,${_noisyExclusions}` : 'dw:*')

  // --- Y.Map.set trap: capture call stacks for direct .set() calls ---
  // Note: Yjs sync echoes (applyUpdate) bypass .set() entirely; this only
  // captures explicit frontend writes like ynode.set('x', ...) in useYCanvas.
  let _ymapSetCount = 0
  const _origYMapSet = Y.Map.prototype.set
  Y.Map.prototype.set = function (key: string, value: DebugValue) {
    _ymapSetCount++
    if (_ymapSetCount <= 20 || _ymapSetCount % 100 === 0) {
      try {
        const stack = new Error().stack
        const caller = stack
          ?.split('\n')
          .slice(2, 12)
          .map((l) => l.trim())
          .join(' <- ')
        const summary =
          typeof value === 'string'
            ? value.length > 80
              ? value.slice(0, 80) + `...(${value.length})`
              : value
            : typeof value === 'number' || typeof value === 'boolean'
              ? value
              : `[${typeof value}]`
        _enqueue('ymap:set', [{ key, summary, caller, n: _ymapSetCount }])
      } catch {
        /* never break Y.Map.set */
      }
    }
    return _origYMapSet.call(this, key, value)
  }
}

// ---------------------------------------------------------------------------
// Structured logging helper — use for NAD debug calls
// ---------------------------------------------------------------------------

/**
 * Create a structured-logging-aware debug function.
 *
 * Wraps `debug` to support both printf-style and structured object calls.
 * When `VITE_NAD_DEBUG` is set, structured calls emit `[nad-json]` lines
 * that `trace-summary.py` can parse.
 *
 * @example
 * ```ts
 * const log = createNadDebug('dw:chat:dedup')
 *
 * // Printf-style (backward compatible, auto-parsed):
 * log('state dedup: prev=%d incoming=%d', prev.length, incoming.length)
 *
 * // Structured (preferred for NAD):
 * log({ event: 'state-dedup', prev: 5, incoming: 3 })
 * ```
 */
export function createNadDebug(namespace: string, options: { priority?: boolean } = {}) {
  const dbg = createDebug(namespace)

  function nadLog(msgOrData: string | StructuredDebugRecord, ...args: DebugValue[]): void {
    if (typeof msgOrData !== 'string') {
      // Structured call — emit the object data directly as JSON,
      // and also pass a human-readable summary to debug() for DevTools
      const { event, ...rest } = msgOrData
      const pairs = Object.entries(rest)
        .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
        .join(' ')
      // Suppress the override's [nad-json] emission — we handle it below
      _suppressOverrideNadJson = true
      dbg(`${String(event)}: ${pairs}`)
      _suppressOverrideNadJson = false
      // Emit structured JSON with the original object data
      if (import.meta.env?.VITE_NAD_DEBUG) {
        const safeData: DebugRecord = { event }
        for (const [k, v] of Object.entries(rest)) {
          safeData[k] = _summarizeValue(v)
        }
        _emitNadJson(namespace, safeData, options.priority)
      }
    } else {
      // Printf-style — delegate entirely to debug()
      dbg(msgOrData, ...args)
    }
  }

  // Expose the underlying debug instance for .enabled checks
  nadLog.enabled = dbg.enabled
  nadLog.namespace = namespace

  return nadLog
}
